import type { BoxProps } from '@material-ui/core/Box';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';

import type { FlowType } from '@jerry-serverless/types';

import type { SelectedServiceType } from '../../types/app_data';
import { isGarageFlowType } from '../../utils/garage';

import PrivacyIcon from './assets/privacy.svg';
import { PolicyType, PrivacyPolicyText } from './PrivacyPolicyText';

interface PrivacyPolicyCardProps extends BoxProps {
  selectedService?: SelectedServiceType;
  flowType?: FlowType;
}

/**
 * Component to render privacy policy card with modal
 */
const UnstyledPrivacyPolicyCard: FunctionComponent<PrivacyPolicyCardProps> = ({
  className,
  selectedService,
  flowType,
  ...boxProps
}): JSX.Element => {
  let content = (
    <Typography className="description">
      We’ve all been there — you sign up to get a quote online, and the next thing you know you’re bombarded with phone
      calls and emails from salespeople. Jerry is different. Our DataLock™ Guarantee ensures you won’t be contacted by
      anyone but us. Please see our&nbsp;
      <PrivacyPolicyText type={PolicyType.PrivacyPolicy}>Privacy Policy</PrivacyPolicyText>
      &nbsp;for information about how we collect, use and share personal data.
    </Typography>
  );
  if (isGarageFlowType(flowType)) {
    content = (
      <Typography className="description">
        Jerry protects your data. Our DataLock™ Guarantee ensures when you use Jerry’s GarageGuard services, you won’t
        be contacted by anyone but us. We never sell your information to telemarketers. Please see our&nbsp;
        <PrivacyPolicyText type={PolicyType.PrivacyPolicy}>Privacy Policy</PrivacyPolicyText>
        &nbsp;for information about how we collect, use and share personal data.
      </Typography>
    );
  }

  return (
    <Box className={className}>
      <Box display="flex" mb={0.5}>
        <Typography className="title">DataLock™</Typography>

        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={PrivacyIcon} style={{ marginLeft: 8 }} alt="Privacy Icon" />
      </Box>
      <Box>{content}</Box>
    </Box>
  );
};

export const PrivacyPolicyCard = styled(UnstyledPrivacyPolicyCard)`
  border-radius: 0.5rem;
  background-color: transparent;
  display: block;
  flex-direction: row;
  align-items: start;
  text-size-adjust: none;

  .title {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.125rem;
  }

  .description {
    color: #6e7e91;
    font-size: 0.75rem;
    line-height: normal;
  }
`;
